import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Box,
  Typography,
  Tooltip,
  Drawer,
  Container,
} from '@mui/material';

import { Divider, Table, TableBody, TableCell, TableHead, TableRow, tableCellClasses } from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { GridColDef, GridRowParams, GridRenderCellParams } from '@mui/x-data-grid';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchExpensePayments,
  deleteExpensePayment,
  exportExpenseReport,
  fetchExpenseCategories,
  fetchExpenseVendors
} from '../../store/expenseStore/expenseThunk';
import { ApiStatus } from '../../utils/api.types';
import { ExpenseFilters, ExpensePayment } from '../../utils/expense.types';
import { Utils } from '../../utils/common-utils';
import CustomDataGrid from '../DataGrid/DataGrid';
import ExpenseFilter from './ExpenseFilter';
import DialogAddExpenseForm from './DialogAddExpenseForm';

import styled_ from "@emotion/styled";

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: '#eaecf0',
    backgroundColor: '#fff',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0, // <-- KEY
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#fbfbfb',
    // border: '0.5px dashed #9da7b7',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: '#e9ecf1',
    // border: '0.5px dashed #9da7b7',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
  },
}));



interface ExpenseListProps {
  accountType?: string;
}

const ExpenseList: FC<ExpenseListProps> = (props) => {
  const dispatch = useAppDispatch();
  const { payments, categories, categoryDropdownItems, vendors, status, errorMessage } = useAppSelector(state => state.expense);
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [filters, setFilters] = useState<ExpenseFilters>({});
  const [openExpenseDrawer, setOpenExpenseDrawer] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpensePayment | null>(null);
  const [isRefresh, setIsRefresh] = useState('');

  // Memoized callback for filter changes
  const handleFiltersChange = useCallback((newFilters: ExpenseFilters) => {
    setFilters(newFilters);
  }, []);

  // Load initial data on component mount
  useEffect(() => {
    if (selectedAccount) {
      dispatch(fetchExpenseCategories({ flat: false }));
      dispatch(fetchExpenseVendors({ accountId: selectedAccount._id }));
    }
  }, [dispatch, selectedAccount]);

  // Load expenses when filters or refresh changes
  useEffect(() => {
    console.log("Got filters in listing page =======> ", filters)
    if (selectedAccount && filters) {
      console.log("Got filters in listing page =======> ", filters)
      dispatch(fetchExpensePayments(filters));
    }
  }, [dispatch, selectedAccount, isRefresh]);

  const handleRefresh = () => {
    setIsRefresh(Date.now().toString());
  };

  const handleOpenExpense = () => setOpenExpenseDrawer(true);

  const handleCloseExpense = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenExpenseDrawer(false);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await dispatch(deleteExpensePayment(expenseId)).unwrap();
      handleRefresh();
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  const handleExport = async (format: 'excel' | 'pdf' = 'excel') => {
    try {
      await dispatch(exportExpenseReport({ format, filters })).unwrap();
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const getPaymentModeColor = (mode: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' } = {
      'CASH': 'success',
      'CHEQUE': 'primary',
      'CARD': 'secondary',
      'UPI': 'info',
      'NETBANKING': 'warning',
    };
    return colors[mode] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' } = {
      'PAID': 'success',
      'BOOKED': 'warning',
      'EXPENSE_RECORDED': 'info',
    };
    return colors[status] || 'default';
  };

  const getCategoryName = (categoryCode: string) => {
    const category = categories?.find(cat => cat.code === categoryCode);
    return category?.name || categoryCode;
  };

  /**
   * Column definitions for expense payments data grid.
   */
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "paymentDate",
      headerName: 'Date',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <>
            {moment(row.paymentDate).format("DD MMM YYYY")}
            <br />
            <Typography variant="caption" color="textSecondary">
              TXN #{row.txnId}
            </Typography>
          </>
        );
      }
    },
    {
      field: "paymentTo",
      headerName: 'Vendor',
      type: "string",
      flex: 2,
      renderCell: ({ row }: GridRenderCellParams) => {
        const vendorName = row.paymentTo?.legalName || row.paymentTo?.name || 'Unknown Vendor';
        return (
          <Tooltip title={vendorName}>
            <Typography variant="body2">
              {Utils.getSubstringWithDots(vendorName, 30)}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "expenseType",
      headerName: 'Category',
      type: "string",
      flex: 1.5,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Typography variant="body2">
            {getCategoryName(row.expenseType)}
          </Typography>
        );
      }
    },
    {
      field: "amount",
      headerName: 'Amount',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Typography variant="body2" fontWeight="medium">
            {Utils.formatNumber(parseFloat(row.amount) || 0, true, true)}
          </Typography>
        );
      }
    },
    {
      field: "paymentMode",
      headerName: 'Payment Mode',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Chip
            label={row.paymentMode}
            size="small"
            color={getPaymentModeColor(row.paymentMode)}
            variant="outlined"
          />
        );
      }
    },
    {
      field: "paymentStatus",
      headerName: 'Status',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const displayStatus = row.applyStatus === 'EXPENSE_RECORDED' ? 'RECORDED' : row.paymentStatus;
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
            variant="filled"
          />
        );
      }
    },
    {
      field: "remarks",
      headerName: 'Remarks',
      type: "string",
      flex: 1.5,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Tooltip title={row.remarks || 'No remarks'}>
            <Typography variant="body2">
              {row.remarks ? Utils.getSubstringWithDots(row.remarks, 25) : '-'}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "actions",
      headerName: 'Actions',
      type: "actions",
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // Handle actions menu
            }}
          >
            <MoreVertIcon />
          </IconButton>
        );
      }
    }
  ], [categories]);

  const handleRowClick = (params: GridRowParams) => {
    setSelectedExpense(params.row as ExpensePayment);
  };

  if (status === ApiStatus.LOADING && !payments?.length) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box>

      <Container maxWidth="xl" sx={{ py: 1 }}>
        <Box mb={4}>
          <Typography variant="h4" component="h1" sx={{ mt: 0, mb: 1 }}>
            Expense Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Record and track vendor payments and miscellaneous expenses
          </Typography>
        </Box>
        <Box display="flex" gap={1} style={{float: 'right'}}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('excel')}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenExpense}
            variant="contained"
            size="small"
          >
            Record Expense
          </Button>
        </Box>
        
      </Container>

      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1" sx={{ mt: 2, mb: 1 }}>
            Expense Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Record and track vendor payments and miscellaneous expenses
          </Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('excel')}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenExpense}
            variant="contained"
            size="small"
          >
            Record Expense
          </Button>
        </Box>
      </Box> */}


      <Grid container spacing={2} >
        <Grid item xs={12}>
          <Box sx={{ width: '100%' }} className={'c-card2'}>
            <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7' }}>
              <Grid xs={10} >
                <ExpenseFilter
                  onFiltersChange={handleFiltersChange}
                  currentFilters={filters}
                  categories={categories || []}
                />
              </Grid>
              <Grid xs={2} >

              </Grid>
            </Grid>
            {true ?
              <>
                <div style={{ marginBottom: 12 }} id='pdfContainer'>
                  <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Date </StyledTableCell>
                        <StyledTableCell>Party & Reference</StyledTableCell>
                        <StyledTableCell>Expense Type</StyledTableCell>
                        <StyledTableCell>Payment Mode</StyledTableCell>
                        <StyledTableCell>Amount</StyledTableCell>
                        <StyledTableCell>Payment Status</StyledTableCell>
                        <StyledTableCell>Remarks</StyledTableCell>
                        <StyledTableCell>Action</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(payments && payments.length > 0) ? payments.map((row: any, index: any) =>
                        <>
                          <StyledTableRow key={index}>
                            {/* Combined Date & Voucher Column */}
                            <StyledTableCell>
                              {(row.paymentDate && row.paymentDate != '') ? (
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                      {moment(row.paymentDate).format("DD MMM YYYY")}
                                    </span>
                                    <span style={{ fontSize: '8pt', color: '#6b7280', fontStyle: 'italic' }}>
                                      {moment(row.paymentDate).format("hh:mm A")}
                                    </span>
                                  </div>
                                  <Chip
                                    label={`Payment`}
                                    size="small"
                                    style={{
                                      fontSize: '7pt',
                                      height: '18px',
                                      backgroundColor: '#7c2d12',
                                      color: 'white'
                                    }}
                                  />
                                </div>
                              ) : '-'}
                            </StyledTableCell>
                            {/* Combined Party & Reference Column */}
                            <StyledTableCell>
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                                <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                  {row.paymentTo.legalName}
                                </span>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell>
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                                <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                  {row?.expenseCategory?.name || ''}
                                </span>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell style={{ fontStyle: 'italic' }}>{row.paymentMode ? Utils.capitalizeFirstLetter(row.paymentMode.toLowerCase()) : '-'}</StyledTableCell>
                            <StyledTableCell>
                              {row.amount > 0 ? `${Utils.formatNumber(parseFloat(row.amount).toFixed(2))}` : ''}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.paymentStatus || ''}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.remarks || ''}
                            </StyledTableCell>
                            <StyledTableCell>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle actions menu
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </StyledTableCell>




                          </StyledTableRow>

                        </>
                      ) :
                        <StyledTableRow>
                          <StyledTableCell colSpan={8} style={{ textAlign: 'center' }}>No Data Found</StyledTableCell>
                        </StyledTableRow>
                      }

                    </TableBody>
                  </Table>
                </div>
              </> :
              <>
                <Grid container p={2} >
                  <Grid xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                  </Grid>
                </Grid>
              </>
            }
          </Box>

        </Grid>
      </Grid>

      {/* Expense Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openExpenseDrawer}
      >
        <DialogAddExpenseForm
          title="Record Expense Payment"
          open={openExpenseDrawer}
          onClose={handleCloseExpense}
        />
      </Drawer>
    </Box>
  );
};

export default ExpenseList;